import React, { useEffect, useState } from 'react';
import {
    Container,
    Box,
    Paper,
    Typography,
    Grid,
    ThemeProvider,
    LinearProgress,
    useMediaQuery
} from '@mui/material';
import { useParams } from 'react-router-dom';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NavBar from '../../layout/NavBar';
import NavBarBot from '../../layout/NavBarBot';
import HeaderCard from '../../layout/HeaderCard';
import { TrainingApi } from '../../../services/api';
import { useAuth } from '../../context/AuthContext';
import { darkTheme } from '../../../theme/darkTheme';
import { Navigate } from 'react-router-dom';
import BackButton from '../../util/buttons/BackButton';
import LoadingPage from '../../layout/LoadingPage';
import HighscoreFilter from './components/allhighscores/HighscoreFilter';
import HighscoreCard from './components/allhighscores/HighscoreCard';

function AllHighscores() {
    const [message, setMessage] = useState({ type: "", text: "" });
    const isMobile = useMediaQuery(darkTheme.breakpoints.down('md'));
    const { gruppeId } = useParams();
    const { nutzer, isLoggedIn, loading: authLoading } = useAuth();
    const [highscores, setHighscores] = useState([]);
    const [filteredHighscores, setFilteredHighscores] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter States
    const [selectedZielmuskel, setSelectedZielmuskel] = useState('alle');
    const [selectedNutzer, setSelectedNutzer] = useState('alle');

    // Unique values for filters
    const [zielmuskeln, setZielmuskeln] = useState([]);
    const [nutzerListe, setNutzerListe] = useState([]);

    useEffect(() => {
        if (!isLoggedIn) {
            setLoading(false);
            return;
        }
        fetchHighscores();
    }, [isLoggedIn, gruppeId]); // gruppeId als Dependency

    useEffect(() => {
        applyFilters();
    }, [selectedZielmuskel, selectedNutzer, highscores]);

    const fetchHighscores = async () => {
        setLoading(true);
        try {
            const response = await TrainingApi.getDashboardStats(nutzer.id, gruppeId ? { gruppeId } : {});

            const data = response.data?.highscores || [];

            setHighscores(data);
            setFilteredHighscores(data);

            // Extrahiere unique Werte für Filter
            const uniqueZielmuskeln = [...new Set(data.map(h => h.zielmuskel).filter(Boolean))];
            const uniqueNutzer = [...new Set(data.map(h => `${h.vname} ${h.nname}`).filter(n => n.trim()))];

            setZielmuskeln(uniqueZielmuskeln.sort());
            setNutzerListe(uniqueNutzer.sort());

        } catch (err) {
            console.error('Fehler beim Laden der Highscores:', err);
            setMessage({ type: "error", text: err });
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...highscores];

        if (selectedZielmuskel !== 'alle') {
            filtered = filtered.filter(h => h.zielmuskel === selectedZielmuskel);
        }

        if (selectedNutzer !== 'alle') {
            filtered = filtered.filter(h => `${h.vname} ${h.nname}` === selectedNutzer);
        }

        setFilteredHighscores(filtered);
    };

    const handleResetFilters = () => {
        setSelectedZielmuskel('alle');
        setSelectedNutzer('alle');
    };

    if (authLoading) {
        return (
            <LoadingPage />
        );
    }

    if (!isLoggedIn) {
        return <Navigate to="/login" replace />;
    }

    return (
        <ThemeProvider theme={darkTheme}>
            <Box
                sx={{
                    bgcolor: 'background.default',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    pb: 4,
                }}
            >
                <NavBar />
                <Container
                    maxWidth="lg"
                    sx={{
                        pt: { xs: 2, md: 4 },
                        px: { xs: 1, sm: 2 },
                        flexGrow: 1,
                        pb: '64px',
                    }}
                >
                    {!isMobile && (
                        <BackButton />
                    )}
                    <HeaderCard
                        title="Alle Highscores"
                        icon={<EmojiEventsIcon />}
                    />

                    {/* Filter Section */}
                    <HighscoreFilter
                        selectedZielmuskel={selectedZielmuskel}
                        onZielmuskel={setSelectedZielmuskel}
                        selectedNutzer={selectedNutzer}
                        onNutzer={setSelectedNutzer}
                        zielmuskeln={zielmuskeln}
                        nutzerListe={nutzerListe}
                        onReset={handleResetFilters}
                        resultCount={filteredHighscores.length}
                    />

                    {/* Highscores Grid */}
                    {loading ? (
                        <LinearProgress />
                    ) : message.type === "error" ? (
                        <Typography color="error" align="center" sx={{ py: 4 }}>
                            {message.text}
                        </Typography>
                    ) : filteredHighscores.length === 0 ? (
                        <Paper
                            sx={{
                                p: 4,
                                backgroundColor: '#1f2937',
                                borderRadius: '16px',
                                textAlign: 'center',
                            }}
                        >
                            <Typography color="#93c5fd" variant="body1">
                                Keine Highscores gefunden
                            </Typography>
                            <Typography color="#93c5fd" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                Trainiere mehr, um deine ersten Highscores zu erreichen! 💪
                            </Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={2} sx={{ display: !isMobile && ("flex"), justifyContent: !isMobile && ("space-evenly") }}>
                            {filteredHighscores.map((score, index) => (
                                <Grid key={index} sx={{ width: isMobile ? ("100%") : ("45%") }}>
                                    <HighscoreCard score={score} />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Container>
            </Box>
            <NavBarBot />
        </ThemeProvider >
    );
}

export default AllHighscores;
